// Deterministic claim-or-cash decision model — the financial sibling of
// functions/estimate/engine.ts, and built the same way: no AI, no network, and
// every number and every user-facing string sourced from data/claim-rules.json,
// so the same answers always produce the same verdict and the SPA and the
// public report page can never drift apart in wording.
//
// It runs over a FINISHED estimate. The repair range is its main input, which is
// why it can't live in the wizard: there is nothing to compare against until the
// pricing guide exists.
//
// The model, in one line:
//
//   claimCost = deductible + (annual premium x surcharge %) x surcharge years
//   cashCost  = the repair range, plus the expected supplement when hidden-damage
//               risk is moderate or high — out of pocket, that growth is yours;
//               on a claim it lands on the insurer above the deductible.
//
// Then it compares the RANGE against the point, and refuses to pick a side when
// the range straddles it — reporting the break-even bill instead. Claiming more
// precision than the estimate supports would be the one way to make this
// actively harmful.
import type {
  ClaimAnswers,
  ClaimDecision,
  ClaimMath,
  ClaimQuestionId,
  ClaimVerdict,
  Estimate,
} from "./types";
import rules from "../data/claim-rules.json";

export const CLAIM_RULES_VERSION = rules.version;

const usd = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const round10 = (n: number) => Math.round(n / 10) * 10;

// Same {placeholder} convention the drivability ruleset uses, so all wording
// stays editable as data. An unknown key is left untouched rather than blanked:
// a visible "{foo}" in staging is a better failure than a silently mangled
// sentence in production.
function fill(template: string, vars: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (m, key: string) => vars[key] ?? m);
}

// The card has nothing to work with when the engine fell through to its
// inspection-only path (engine.ts step 6): there is no repair scope to weigh,
// so a claim-or-cash verdict would be invented rather than computed.
export function claimDecisionApplies(estimate: Estimate): boolean {
  return estimate.damagedParts.length > 0;
}

// Question order, with the short-circuits that make the median user answer one
// or two questions rather than four. `undefined` means "not asked yet";
// `deductible: null` means "asked, and they don't know" — the two must stay
// distinct here or the loop never terminates.
export function nextClaimQuestion(a: ClaimAnswers): ClaimQuestionId | null {
  if (!a.fault) return "fault";
  // Someone else's liability coverage owes the repair. Nothing about the user's
  // own deductible or premium changes that, so stop asking.
  if (a.fault === "other-driver") return null;
  if (!a.coverage) return "coverage";
  // No coverage that repairs their own car — there is no claim path to weigh.
  if (a.coverage === "liability-only") return null;
  if (a.deductible === undefined) return "deductible";
  if (!a.ownership) return "ownership";
  return null;
}

// Ordered for resetFrom(): re-answering a question invalidates everything after
// it, because the later questions are only reachable through the earlier ones.
export const CLAIM_QUESTION_ORDER: ClaimQuestionId[] = [
  "fault",
  "coverage",
  "deductible",
  "ownership",
];

// Clear one answer and every answer downstream of it. The optional extras
// (vehicle value, premium) survive: they are inputs to the verdict, not steps
// in the question flow.
export function resetFrom(a: ClaimAnswers, q: ClaimQuestionId): ClaimAnswers {
  const from = CLAIM_QUESTION_ORDER.indexOf(q);
  const next: ClaimAnswers = { ...a };
  for (const id of CLAIM_QUESTION_ORDER.slice(from)) delete next[id];
  return next;
}

// The repair bill to plan around: the estimate's range, carrying its own
// supplement risk. Only moderate/high bands add it — at low risk the ruleset's
// supplement band starts at $0 and adding it would just widen the range for no
// informational gain.
//
// Exported because the total-loss card needs the same figure (carriers total on
// the post-teardown bill, not the first estimate). Sharing it is what guarantees
// the two cards on the results page can never quote different repair numbers.
export function plannedRepairRange(estimate: Estimate): {
  range: { low: number; high: number };
  supplement?: { low: number; high: number };
} {
  const base = estimate.totalCostRange;
  const supp = estimate.hiddenDamage.expectedSupplementRange;
  const carries =
    !!supp && supp.high > 0 && (estimate.hiddenDamage.band === "moderate" || estimate.hiddenDamage.band === "high");
  if (!carries || !supp) return { range: { low: base.low, high: base.high } };
  return {
    range: { low: base.low + supp.low, high: base.high + supp.high },
    supplement: { low: supp.low, high: supp.high },
  };
}

function buildMath(estimate: Estimate, a: ClaimAnswers): { math: ClaimMath; supplement?: { low: number; high: number } } {
  const deductibleAssumed = a.deductible === null || a.deductible === undefined;
  const deductible = deductibleAssumed ? rules.deductible.assumedWhenUnknown : (a.deductible as number);

  const premiumAssumed = a.annualPremium == null;
  const annualPremium = premiumAssumed ? rules.premium.defaultAnnualFullCoverage : (a.annualPremium as number);

  // An animal strike, hail or a falling branch is a comprehensive loss, and
  // carriers surcharge those far more lightly than an at-fault collision.
  // "unsure" fault deliberately prices as at-fault: it is the expensive
  // assumption, and the output says so rather than quietly flattering the user.
  const kind = a.fault === "not-collision" ? "comprehensive" : "at-fault";
  const s = rules.surcharge[kind];
  const surchargeAnnual = round10(annualPremium * s.pct);
  const surchargeTotal = surchargeAnnual * s.years;
  const claimCost = deductible + surchargeTotal;

  const cash = plannedRepairRange(estimate);
  return {
    supplement: cash.supplement,
    math: {
      deductible,
      deductibleAssumed,
      annualPremium,
      premiumAssumed,
      surchargeAnnual,
      surchargeYears: s.years,
      surchargeTotal,
      claimCost,
      cashCost: cash.range,
      breakEven: claimCost,
    },
  };
}

// Compare the range against the point. The middle case is the honest one and
// the most common: when the range straddles claimCost, neither path is provably
// cheaper, and saying so plus the break-even number is worth more than a
// confident coin-flip.
function verdictFor(math: ClaimMath): ClaimVerdict {
  if (math.cashCost.high < math.claimCost) return "likely-cash";
  if (math.cashCost.low > math.claimCost) return "likely-claim";
  return "close-call";
}

export function assessClaim(estimate: Estimate, a: ClaimAnswers): ClaimDecision {
  const copy = rules.copy;
  const base = {
    rulesVersion: rules.version,
    disclaimer: copy.disclaimer,
  };

  // Always-on notes, appended to every verdict: the notify-vs-file distinction,
  // the CLUE record, and Washington's separate legal duty to report. These are
  // the guardrails — no branch is allowed to drop them.
  const alwaysPoints = copy.always.map((t) =>
    fill(t, {
      clueYears: String(rules.clueYears),
      reportingThreshold: usd(rules.reportingThreshold),
    }),
  );

  const pending = nextClaimQuestion(a);
  if (pending) {
    const v = copy.verdicts.incomplete;
    return { ...base, verdict: "incomplete", headline: v.headline, summary: v.summary, points: [], nextStep: v.nextStep };
  }

  // --- Branches that never reach the arithmetic ---------------------------
  if (a.fault === "other-driver") {
    const v = copy.verdicts["third-party"];
    return {
      ...base,
      verdict: "third-party",
      headline: v.headline,
      summary: v.summary,
      points: [...v.points, ...alwaysPoints],
      nextStep: v.nextStep,
    };
  }

  if (a.coverage === "liability-only") {
    const v = copy.verdicts["cash-only"];
    return {
      ...base,
      verdict: "cash-only",
      headline: v.headline,
      summary: v.summary,
      points: [...v.points, ...alwaysPoints],
      nextStep: v.nextStep,
    };
  }

  // --- The real decision --------------------------------------------------
  const { math, supplement } = buildMath(estimate, a);
  const verdict = verdictFor(math);
  const v = copy.verdicts[verdict as "likely-cash" | "likely-claim" | "close-call"];

  const vars: Record<string, string> = {
    cashLow: usd(math.cashCost.low),
    cashHigh: usd(math.cashCost.high),
    claimCost: usd(math.claimCost),
    breakEven: usd(math.breakEven),
    deductible: usd(math.deductible),
    surchargeAnnual: usd(math.surchargeAnnual),
    surchargeTotal: usd(math.surchargeTotal),
    surchargeYears: String(math.surchargeYears),
    hiddenBand: estimate.hiddenDamage.band,
    supplementLow: supplement ? usd(supplement.low) : "",
    supplementHigh: supplement ? usd(supplement.high) : "",
  };

  const m = copy.modifiers;
  const points: string[] = v.points.map((t) => fill(t, vars));

  // Modifiers, ordered by how much they move the decision.
  if (supplement) points.push(fill(m.supplementIncluded, vars));
  if (estimate.hiddenDamage.band === "high") points.push(fill(m.hiddenDamage, vars));
  if (a.fault === "not-collision") points.push(m.comprehensive);
  if (a.fault === "unsure") points.push(m.unsureFault);
  if (a.coverage === "unsure") points.push(m.unsureCoverage);
  if (math.deductibleAssumed) points.push(fill(m.unknownDeductible, vars));
  if (a.ownership === "financed") points.push(m.financed);
  if (a.ownership === "leased") points.push(m.leased);

  // Optional total-loss check — only when the user volunteered a value. Past
  // the threshold the question stops being claim-or-cash altogether, so it
  // surfaces above the verdict rather than as one more bullet.
  let totalLossWarning: string | undefined;
  if (a.vehicleValue && a.vehicleValue > 0) {
    const threshold = a.vehicleValue * rules.totalLoss.thresholdPct;
    if (math.cashCost.high >= threshold) {
      totalLossWarning = fill(m.totalLoss, {
        ...vars,
        vehicleValue: usd(a.vehicleValue),
        thresholdPct: String(Math.round(rules.totalLoss.thresholdPct * 100)),
      });
    }
  }

  return {
    ...base,
    verdict,
    headline: v.headline,
    summary: fill(v.summary, vars),
    math,
    totalLossWarning,
    points: [...points, ...alwaysPoints],
    nextStep: fill(v.nextStep, vars),
  };
}

// Copy exported for the UI layers: the teaser text, question prompts and the
// uninsured variant all live in the ruleset too, so the SPA card and the
// server-rendered report page cannot drift apart.
export const CLAIM_COPY = rules.copy;
export const CLAIM_QUESTIONS = rules.questions;
